import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, urlImagem } from '../lib/api';

interface ProximaOS {
  id: number;
  codigo: string;
  tipo_os: string;
  status: string;
  avaria?: string | null;
  descricao?: string | null;
  data_agendada?: string | null;
  hora_agendada?: string | null;
  tecnico_nome?: string | null;
}

interface AtivoPublicoData {
  empresa: {
    id: number;
    nome: string;
    logo_url?: string | null;
    logo_url_publica?: string | null;
  };
  rotulo_numero_serie: string;
  ativo: {
    id: number;
    nome: string;
    status: string;
    patrimonio?: string | null;
    marca?: string | null;
    fabricante?: string | null;
    modelo?: string | null;
    numero_serie?: string | null;
    qr_code?: string | null;
    foto_url?: string | null;
    foto_url_publica?: string | null;
    unidade_nome?: string | null;
    piso_nome?: string | null;
    local_nome?: string | null;
    categoria_nome?: string | null;
  };
  proximas_os: ProximaOS[];
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return 'LF';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0] || ''}${partes[partes.length - 1][0] || ''}`.toUpperCase();
}

function detalheChip(rotulo: string, valor?: string | null) {
  if (!valor) return null;
  return (
    <div className="ativo-publico-chip" key={`${rotulo}-${valor}`}>
      <span>{rotulo}</span>
      <strong>{valor}</strong>
    </div>
  );
}

function rotuloStatus(status: string): string {
  const mapa: Record<string, string> = {
    aberto: 'Aberto',
    em_andamento: 'Em execução',
    interrompido: 'Interrompido',
    aguardando_aprovacao: 'Aguardando aprovação',
    concluido: 'Concluído',
    cancelado: 'Cancelado',
  };
  return mapa[status] || status;
}

function rotuloTipo(tipo: string): string {
  const mapa: Record<string, string> = {
    corretiva: 'Corretiva',
    preventiva: 'Preventiva',
  };
  return mapa[tipo] || tipo;
}

function dataCurta(data?: string | null): string {
  if (!data) return '-';
  const m = String(data).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(data);
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function agendamentoLabel(data?: string | null, hora?: string | null): string {
  if (!data) return 'Sem agendamento';
  const horaCurta = hora ? String(hora).slice(0, 5) : 'Sem horário';
  return `${dataCurta(data)} • ${horaCurta}`;
}

export default function AtivoPublico() {
  const { empresaId, codigo } = useParams();
  const [dados, setDados] = useState<AtivoPublicoData | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setErro('');
    setDados(null);
    api<AtivoPublicoData>(
      `/ativos/ativo_publico.php?e=${encodeURIComponent(empresaId || '')}&codigo=${encodeURIComponent(codigo || '')}`,
      { includeAuth: false, redirectOn401: false },
    )
      .then(setDados)
      .catch((err) => setErro(err instanceof Error ? err.message : String(err)));
  }, [empresaId, codigo]);

  if (erro) {
    return (
      <div className="ativo-publico-shell">
        <div className="ativo-publico-wrap">
          <section className="ativo-publico-error">
            <div className="ativo-publico-kicker">QR público</div>
            <h1>Não foi possível abrir este equipamento</h1>
            <p>{erro}</p>
            <Link to="/" className="btn">Ir para a página inicial</Link>
          </section>
        </div>
      </div>
    );
  }

  if (!dados) {
    return (
      <div className="ativo-publico-shell">
        <div className="ativo-publico-wrap">
          <section className="ativo-publico-loading">
            <div className="loading-orbs"><i /><i /><i /></div>
            <strong>Carregando os detalhes do equipamento...</strong>
          </section>
        </div>
      </div>
    );
  }

  const { empresa, ativo, rotulo_numero_serie, proximas_os } = dados;
  const localizacao = [ativo.unidade_nome, ativo.piso_nome, ativo.local_nome].filter(Boolean).join(' • ');
  const logoEmpresa = empresa.logo_url_publica || urlImagem(empresa.logo_url);
  const fotoEquipamento = ativo.foto_url_publica || urlImagem(ativo.foto_url);

  return (
    <div className="ativo-publico-shell">
      <div className="ativo-publico-wrap">
        <section className="ativo-publico-hero">
          <div className="ativo-publico-brand">
            {logoEmpresa ? (
              <img className="ativo-publico-logo" src={logoEmpresa} alt={empresa.nome} />
            ) : (
              <div className="ativo-publico-logo ativo-publico-logo-fallback">{iniciais(empresa.nome)}</div>
            )}
            <div>
              <div className="ativo-publico-kicker">QR público do equipamento</div>
              <h1>{ativo.nome}</h1>
              <p>{empresa.nome}</p>
            </div>
          </div>
          <div className={`ativo-publico-status st-${ativo.status || 'ativo'}`}>
            {ativo.status || 'ativo'}
          </div>
        </section>

        <section className="ativo-publico-grid">
          <article className="ativo-publico-card ativo-publico-photo-card">
            <div className="ativo-publico-card-head">
              <span>Imagem</span>
              {ativo.categoria_nome && <strong>{ativo.categoria_nome}</strong>}
            </div>
            <div className="ativo-publico-photo-frame">
              {fotoEquipamento ? (
                <img src={fotoEquipamento} alt={ativo.nome} className="ativo-publico-photo" />
              ) : (
                <div className="ativo-publico-photo-placeholder">
                  <span>{iniciais(ativo.nome)}</span>
                  <small>Sem foto cadastrada</small>
                </div>
              )}
            </div>
          </article>

          <article className="ativo-publico-card">
            <div className="ativo-publico-card-head">
              <span>Ficha técnica</span>
              <strong>{ativo.qr_code}</strong>
            </div>
            <div className="ativo-publico-chip-grid">
              {detalheChip('Patrimônio', ativo.patrimonio)}
              {detalheChip('Marca', ativo.marca)}
              {detalheChip('Fabricante', ativo.fabricante)}
              {detalheChip('Modelo', ativo.modelo)}
              {detalheChip(rotulo_numero_serie || 'Número de série', ativo.numero_serie)}
            </div>
          </article>

          <article className="ativo-publico-card ativo-publico-location-card">
            <div className="ativo-publico-card-head">
              <span>Localização</span>
              <strong>Onde este equipamento está</strong>
            </div>
            <div className="ativo-publico-location-list">
              <div>
                <label>Unidade</label>
                <strong>{ativo.unidade_nome || '-'}</strong>
              </div>
              <div>
                <label>Piso</label>
                <strong>{ativo.piso_nome || '-'}</strong>
              </div>
              <div>
                <label>Local</label>
                <strong>{ativo.local_nome || '-'}</strong>
              </div>
            </div>
            {localizacao && <div className="ativo-publico-location-ribbon">{localizacao}</div>}
          </article>
        </section>

        <section className="ativo-publico-os-board">
          <article className="ativo-publico-card ativo-publico-os-card">
            <div className="ativo-publico-card-head">
              <span>Próximas OS</span>
              <strong>{proximas_os.length ? `${proximas_os.length} agendada(s)` : 'Sem agenda futura'}</strong>
            </div>

            {proximas_os.length ? (
              <div className="ativo-publico-os-list">
                {proximas_os.map((os) => (
                  <article className="ativo-publico-os-item" key={os.id}>
                    <div className="ativo-publico-os-top">
                      <div>
                        <strong>{os.codigo}</strong>
                        <span>{rotuloTipo(os.tipo_os)}</span>
                      </div>
                      <div className={`ativo-publico-os-status st-${os.status || 'aberto'}`}>
                        {rotuloStatus(os.status)}
                      </div>
                    </div>

                    <div className="ativo-publico-os-meta">
                      <div>
                        <label>Agendamento</label>
                        <strong>{agendamentoLabel(os.data_agendada, os.hora_agendada)}</strong>
                      </div>
                      <div>
                        <label>Técnico</label>
                        <strong>{os.tecnico_nome || '-'}</strong>
                      </div>
                    </div>

                    {(os.avaria || os.descricao) && (
                      <p className="ativo-publico-os-texto">{os.avaria || os.descricao}</p>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="ativo-publico-os-empty">
                Nenhuma OS futura vinculada a este equipamento a partir de 12/08/2026.
              </p>
            )}
          </article>
        </section>

        <footer className="ativo-publico-footer">
          <span>Consulta pública gerada a partir do QR do equipamento.</span>
          <div className="ativo-publico-footer-brand">
            {logoEmpresa ? (
              <img className="ativo-publico-footer-logo" src={logoEmpresa} alt={empresa.nome} />
            ) : (
              <div className="ativo-publico-footer-logo ativo-publico-logo-fallback">{iniciais(empresa.nome)}</div>
            )}
            <strong>{empresa.nome}</strong>
          </div>
        </footer>
      </div>
    </div>
  );
}
